import { type ILayerLibrary, type ILayerProps } from '@/Root/Types/LayerTypes'
import { type ReactNode } from 'react'
import { type IDrawTool, type IDrawToolPropsOptions, EMapShape } from '@/Root/Types/ToolTypes'
import type DrawToolRoot from '@/Root/Tools/DrawToolRoot'
import { type IEventWithData, type IEventManager, type IEventCallback } from '@axdspub/axiom-ui-utilities'
import type MapRoot from '@/Root/MapRoot'

interface ILatLon {
  lat: number
  lon: number
}

interface ILatLonBoundsProps {
  sw: ILatLon
  ne: ILatLon
}

interface ILatLonBoundsPositionProps {
  val: number
  corner: 'sw' | 'ne'
  position: 'lat' | 'lon'
}

interface ILatLonBounds extends ILatLonBoundsProps {
  toGeoJson: () => GeoJSON.FeatureCollection
  toArray: () => [number, number, number, number]
  toStr: () => string
  fromArray: (a: [number, number, number, number]) => void
  set: (p: ILatLonBoundsProps) => void
  get: () => ILatLonBoundsProps
}

interface IMapProjection {
  id: string
  label: string
  proj4?: string
}

interface IMapLibrary {
  Map: typeof MapRoot
  Tools?: {
    Draw?: typeof DrawToolRoot
  }
  LayerTypes?: ILayerLibrary[]
}

interface IMapState {
  center: ILatLon
  zoom: number
  bounds: ILatLonBounds
}
interface IMapProps {
  center?: ILatLon
  zoom?: number
  baseLayerKey?: string
  baseLayer?: ILayerProps
  layers?: ILayerProps[]
  tools?: {
    draw?: IDrawToolPropsOptions
    polygonQuery?: object | boolean
    projectionChanger?: object | boolean
    timeControl?: object | boolean
  }
  projection?: IMapProjection
  mapLibrary?: IMapLibrary
  mapLibraryKey?: string
  element?: HTMLElement | null
  debug?: boolean
  onMapLoaded?: (mapImplementation: any) => void
  onMapMoveStart?: (mapStateEvent: IMapStateEvent) => void
  onMapViewChange?: (mapStateEvent: IMapStateEvent) => void
}

type StyleObject = Record<string | number, string | number | null>

interface IStyleableMapProps extends IMapProps {
  width?: string
  height?: string
  style?: StyleObject
  defaultClassName?: string
  setState?: (map: IMap) => void
  className?: string
  Tools?: ReactNode
  Legend?: ReactNode
  Overlay?: ReactNode
}

enum EMapTools {
  draw = 'draw'
}

interface IMapStateEvent extends IEventWithData {
  data?: {
    mapState: IMapState
  }
}

interface IMapDrawEvent extends IEventWithData {
  data?: {
    geojson: GeoJSON.FeatureCollection
    shape?: string
  }
}

interface IMapPointerInteractEvent extends IEventWithData {
  data?: {
    point: {
      x: number
      y: number
    }
    windowPoint: {
      x: number
      y: number
    }
    latlon: ILatLon
    color?: string
  }
}

type IMapStateCallback = (e: IMapStateEvent) => void
type IMapDrawCallback = (e: IMapDrawEvent) => void
type IMapPointerCallback = (e: IMapPointerInteractEvent) => void

interface IOverloadedMapOn {
  (eventType: string, callback: IEventCallback): void
  (eventType: 'drawstart', callback: IMapDrawCallback): void
  (eventType: 'drawend', callback: IMapDrawCallback): void
  (eventType: 'drawupdate', callback: IMapDrawCallback): void
  (eventType: 'drawprogress', callback: IMapDrawCallback): void
  (eventType: 'loaded', callback: IMapStateCallback): void
  (eventType: 'movestart', callback: IMapStateCallback): void
  (eventType: 'moveend', callback: IMapStateCallback): void
  (eventType: 'pointermove', callback: IMapPointerCallback): void
  (eventType: 'pointerselect', callback: IMapPointerCallback): void
  (eventType: 'mapclick', callback: IEventCallback): void
}
interface IOverloadedMapOff {
  (eventType: string, callback: IEventCallback): void
  (eventType: 'drawstart', callback: IMapDrawCallback): void
  (eventType: 'drawend', callback: IMapDrawCallback): void
  (eventType: 'drawupdate', callback: IMapDrawCallback): void
  (eventType: 'drawprogress', callback: IMapDrawCallback): void
  (eventType: 'loaded', callback: IMapStateCallback): void
  (eventType: 'movestart', callback: IMapStateCallback): void
  (eventType: 'moveend', callback: IMapStateCallback): void
  (eventType: 'pointermove', callback: IMapPointerCallback): void
  (eventType: 'pointerselect', callback: IMapPointerCallback): void
}

declare interface IMap extends IEventManager {
  center?: ILatLon
  zoom?: number
  baseLayerKey?: string
  baseLayer?: ILayerProps
  layers?:(ILayerProps[] | undefined)
  tools?: {
    draw?: IDrawToolPropsOptions | undefined
    polygonQuery?: object | boolean
    projectionChanger?: object | boolean
    timeControl?: object | boolean
  }

  toolInstances: {
    draw?: IDrawTool
  }
  projection?: IMapProjection
  mapLibrary?: IMapLibrary
  mapLibraryKey?: string
  map?: any

  pointToLatLon: (pt: { x: number, y: number }) => ILatLon
  mapPointToWindowPoint: (pt: { x: number, y: number }) => { x: number, y: number }

  getIDrawToolInstance: () => IDrawTool | undefined

  setBaseLayerKey: (baseLayerKey: string) => void
  getBaseLayerKey: () => string | undefined

  setBaseLayer: (layer: ILayerProps) => void

  addLayer: (layer: ILayerProps) => void
  addLayers: (layers: ILayerProps[]) => void

  removeLayer: (layer: ILayerProps | string) => void
  removeLayers: (layers: Array<ILayerProps | string>) => void

  getLayer: (layerIdentifier: ILayerProps | string) => ILayerProps | undefined
  hasLayer: (layer: ILayerProps | string) => boolean

  setZoom: (zoom: number) => void
  getZoom: () => number | undefined

  setCenter: (center: ILatLon) => void
  getCenter: () => ILatLon | undefined

  setBounds: (bounds: ILatLonBounds) => void
  getBounds: () => ILatLonBounds | undefined | null

  setProjection: (projection: IMapProjection) => void
  getProjection: () => IMapProjection | undefined

  setElement: (element: HTMLElement) => void
  getElement: () => HTMLElement | undefined | null

  setMapBaseLayer: (layer: ILayerProps) => void
  addLayerToMap: (layer: ILayerProps) => Promise<void>
  removeLayerFromMap: (layer: ILayerProps) => void
  setMapZoom: (zoom: number) => void
  getMapZoom: () => number | undefined
  setMapCenter: (center: ILatLon) => void
  getMapCenter: () => ILatLon | undefined | undefined
  setMapBounds: (bounds: ILatLonBounds) => void
  getMapBounds: () => ILatLonBounds | undefined | undefined
  setMapProjection: (projection: IMapProjection) => void
  setMapElement: (element: HTMLElement) => void

  enableDraw: (shape: EMapShape) => void
  disableDraw: (shape: EMapShape) => void
  clearDrawings: () => void
  getDrawGeojson: () => GeoJSON.FeatureCollection | undefined
  setDrawGeojson: (geoJSON: GeoJSON.FeatureCollection) => void

  reloadLayers: (layers: ILayerProps[]) => void

  dispatchDrawStartEvent: (shape: string) => void
  dispatchDrawCompleteEvent: (geoJSON: GeoJSON.FeatureCollection) => void
  dispatchDrawUpdateEvent: (geoJSON: GeoJSON.FeatureCollection) => void
  dispatchDrawProgressEvent: (geoJSON: GeoJSON.FeatureCollection) => void

  destroy: () => void
  isDestroyed: () => boolean

  getMapState: () => IMapState

  onDrawStart: (callback: () => void) => void
  onDrawComplete: (callback: IMapDrawCallback) => void
  onDrawUpdate: (callback: IMapDrawCallback) => void
  onDrawProgress: (callback: IMapDrawCallback) => void
  onMapLoaded: (callback: IMapStateCallback) => void
  onMapMoveStart: (callback: IMapStateCallback) => void
  onMapViewChange: (callback: IMapStateCallback) => void
  onPointerMove: (callback: IMapPointerCallback) => void
  onPointerSelect: (callback: IMapPointerCallback) => void
  on: IOverloadedMapOn
  off: IOverloadedMapOff
}

interface IMapContext {
  map?: IMap
  mapLibrary?: IMapLibrary
}

export type {
  ILatLon,
  ILatLonBoundsProps,
  ILatLonBoundsPositionProps,
  ILatLonBounds,
  IMapLibrary,
  IMap,
  IMapStateEvent,
  IMapDrawEvent,
  IMapStateCallback,
  IMapDrawCallback,
  IMapPointerCallback,
  IMapProps,
  IStyleableMapProps,
  IMapProjection,
  IMapContext,
  IMapState
}

export {
  EMapTools,
  EMapShape
}
